/* eslint-disable no-unused-vars */
import React, { ElementType, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { CaretDown, CaretUp } from 'phosphor-react';
import { FacetOptionRow, FacetSearch } from './FacetOptionList';
import { normalizeText } from './facet';

export interface SearchSelectOption {
  value: string;
  label: string;
}

interface SearchSelectProps {
  options: SearchSelectOption[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  icon?: ElementType;
  searchLabel?: string;
  minSearchCount?: number;
  width?: string;
}

export const SearchSelect = ({
  options,
  value,
  onChange,
  ariaLabel,
  icon,
  searchLabel,
  minSearchCount = 7,
  width = '280px',
}: SearchSelectProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState('');
  const [highlight, setHighlight] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = options.find((option) => option.value === value) ?? options[0];
  const showSearch = options.length >= minSearchCount;

  const visible = useMemo(() => {
    if (!showSearch || search.trim() === '') return options;
    return options.filter((option) => normalizeText(option.label).includes(normalizeText(search)));
  }, [options, search, showSearch]);

  useEffect(() => {
    if (!isOpen) return;
    const index = visible.findIndex((option) => option.value === value);
    setHighlight(index >= 0 ? index : 0);
  }, [isOpen]);

  useEffect(() => {
    setHighlight(0);
  }, [search]);

  useEffect(() => {
    const node = listRef.current?.querySelector(`[data-index="${highlight}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const handleClose = () => {
    onClose();
    setSearch('');
  };

  const select = (next: string) => {
    onChange(next);
    handleClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((prev) => Math.min(prev + 1, visible.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = visible[highlight];
      if (option) select(option.value);
    }
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      placement="bottom-end"
      isLazy
      autoFocus={showSearch}
      returnFocusOnClose={false}
    >
      <PopoverTrigger>
        <Flex
          as="button"
          type="button"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          h="34px"
          px="10px"
          align="center"
          gap="8px"
          maxW={width}
          bg={isOpen ? 'surface.selected' : 'surface.base'}
          border="1px solid"
          borderColor="line.input"
          borderRadius="lg"
          boxShadow="control"
          cursor="pointer"
          fontSize="13px"
          color="content.strong"
          _hover={{ borderColor: '#DCDCDC' }}
          _focusVisible={{ borderColor: 'line.ring', boxShadow: 'outline', outline: 'none' }}
        >
          {icon && <Icon as={icon} w="14px" h="14px" color="content.subtle" flexShrink={0} />}
          <Text as="span" noOfLines={1} textAlign="left">
            {selected?.label}
          </Text>
          <Box
            as="span"
            display="flex"
            flexDirection="column"
            alignItems="center"
            color="content.strong"
            opacity={0.8}
            ml="auto"
            flexShrink={0}
            sx={{ '& > svg + svg': { mt: '-8px' } }}
          >
            <CaretUp size={14} />
            <CaretDown size={14} />
          </Box>
        </Flex>
      </PopoverTrigger>
      <PopoverContent w={width} p={0} overflow="hidden" onKeyDown={handleKeyDown}>
        <PopoverBody p={0}>
          {showSearch && (
            <FacetSearch label={searchLabel ?? ariaLabel} value={search} onChange={setSearch} />
          )}
          <Box ref={listRef} maxH="260px" overflowY="auto" p="6px" role="listbox">
            {visible.length === 0 ? (
              <Text fontSize="13px" color="content.faint" px="6px" py="10px">
                Nenhum resultado.
              </Text>
            ) : (
              visible.map((option, index) => (
                <Box
                  key={option.value}
                  data-index={index}
                  borderRadius="6px"
                  bg={index === highlight ? 'surface.selected' : 'transparent'}
                  onMouseEnter={() => setHighlight(index)}
                >
                  <FacetOptionRow
                    label={option.label}
                    selected={option.value === value}
                    onClick={() => select(option.value)}
                  />
                </Box>
              ))
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
